// Centralized FinVault input/output hardening helpers.
// Keep this file dependency-free so it works in local, cloud, and export flows.

export const FIELD_LIMITS = {
  name: 80,
  profileName: 80,
  title: 80,
  note: 500,
  description: 500,
  category: 50,
  person: 80,
  phone: 20,
  bankName: 80,
  depositName: 80,
  symbol: 20,
  platform: 60,
  feedback: 500,
  id: 120
};

export const ALLOWED_PROFILE_TYPES = ["standard", "lending", "trading", "gold", "silver", "goldsilver", "fdrd"];
export const ALLOWED_STANDARD_TYPES = ["income", "expense"];
export const ALLOWED_LENDING_TYPES = ["lent", "returned", "borrowed", "repaid", "profit"];
export const ALLOWED_TRADE_TYPES = ["deposit", "withdraw", "profit", "loss", "dividend", "buy", "sell"];
export const ALLOWED_METAL_TX_TYPES = ["buy", "sell", "cashback", "adjustment"];
export const ALLOWED_FDRD_TYPES = ["active", "matured", "closedEarly", "renewed", "cancelled", "closed", "withdrawn"];
export const ALLOWED_DEPOSIT_TYPES = ["FD", "RD"];
export const ALLOWED_FEEDBACK_TYPES = ["Bug", "Suggestion", "UI Issue", "Security Concern", "Other"];

// Intentionally strips ASCII control characters from user-controlled text fields.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = new RegExp("[\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F\\u007F]", "g");
const ZERO_WIDTH = /[\u200B-\u200D\uFEFF]/g;
const DANGEROUS_PROTOCOLS = /\b(?:javascript|data|vbscript)\s*:/gi;
const EVENT_HANDLER_TEXT = /\bon\w+\s*=/gi;
const SAFE_HEX_COLOR = /^#[0-9a-f]{6}$/i;
const SAFE_MONTH_KEY = /^\d{4}-\d{2}$/;
const PROFILE_NAME_SAFE_CHARS = /[^\p{L}\p{N}\s._&()-]/gu;

function cleanTextBase(value, maxLength = FIELD_LIMITS.note, { trim = true } = {}) {
  if (value === null || value === undefined) return "";
  let clean = String(value)
    .replace(CONTROL_CHARS, " ")
    .replace(ZERO_WIDTH, "")
    .replace(DANGEROUS_PROTOCOLS, "")
    .replace(EVENT_HANDLER_TEXT, "")
    .replace(/[<>]/g, "")
    .replace(/\s+/g, " ");
  if (trim) clean = clean.trim();
  return clean.slice(0, maxLength);
}

export function sanitizeText(value, maxLength = FIELD_LIMITS.note) {
  return cleanTextBase(value, maxLength, { trim: true });
}

export function sanitizeTextInput(value, maxLength = FIELD_LIMITS.note) {
  // For live typing only: keep a trailing/inner space so normal sentence entry works.
  // Final storage/export sanitizers still trim and protect formula-injection during export.
  return cleanTextBase(value, maxLength, { trim: false });
}

export function sanitizeNote(value, maxLength = FIELD_LIMITS.note) {
  // Notes intentionally allow readable punctuation/symbols such as . , / ' ! @ # $ % & * ( ) _.
  // They are stored as plain text only: HTML angle brackets, dangerous URL protocols,
  // control characters, and inline event-handler text are stripped.
  return sanitizeText(value, maxLength);
}

export function sanitizeNoteInput(value, maxLength = FIELD_LIMITS.note) {
  // Live note input should allow spaces, emoji, numbers, and normal punctuation while typing.
  // Final saved/exported values still pass through sanitizeNote/safeForExport.
  return sanitizeTextInput(value, maxLength);
}

function cleanProfileName(value, maxLength = FIELD_LIMITS.profileName, { trim = true } = {}) {
  // Profile names should be readable labels, not script/formula/punctuation payloads.
  // Unicode letters/numbers are allowed for Indian language names.
  let clean = (trim ? sanitizeText(value, maxLength) : sanitizeTextInput(value, maxLength))
    .replace(PROFILE_NAME_SAFE_CHARS, "")
    .replace(/\s+/g, " ");
  if (trim) {
    // Avoid names made to look like paths/formulas, e.g. ../../, ---name, .hidden
    clean = clean.replace(/^[-._&()\s]+/, "").replace(/[-._&()]{4,}/g, "-").trim();
  } else {
    clean = clean.replace(/^[-._&()]+/, "").replace(/[-._&()]{4,}/g, "-");
  }
  return clean.slice(0, maxLength);
}

export function sanitizeProfileName(value, maxLength = FIELD_LIMITS.profileName) {
  return cleanProfileName(value, maxLength, { trim: true });
}

export function sanitizeProfileNameInput(value, maxLength = FIELD_LIMITS.profileName) {
  return cleanProfileName(value, maxLength, { trim: false });
}



const FEEDBACK_URL_TEXT = /\b(?:https?|ftp):\/\/\S+|\bwww\.\S+|\b\S+\.(?:com|in|net|org|io|app|dev)\S*/gi;
const FEEDBACK_SAFE_CHARS = /[^\p{L}\p{N}\s.,?!\-_]/gu;

export function sanitizeFeedbackType(value) {
  return sanitizeChoice(value, ALLOWED_FEEDBACK_TYPES, "Other");
}

export function sanitizeFeedbackMessageInput(value, maxLength = FIELD_LIMITS.feedback) {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(CONTROL_CHARS, " ")
    .replace(ZERO_WIDTH, "")
    .replace(FEEDBACK_URL_TEXT, "")
    .replace(DANGEROUS_PROTOCOLS, "")
    .replace(EVENT_HANDLER_TEXT, "")
    .replace(FEEDBACK_SAFE_CHARS, "")
    .replace(/\s{3,}/g, "  ")
    .slice(0, maxLength);
}

export function sanitizeFeedbackMessage(value, maxLength = FIELD_LIMITS.feedback) {
  return sanitizeFeedbackMessageInput(value, maxLength).replace(/\s+/g, " ").trim().slice(0, maxLength);
}

export function sanitizeFeedbackForStorage(feedback = {}) {
  const feedbackDate = sanitizeDate(feedback.feedbackDate) || new Date().toISOString().split("T")[0];
  const safe = {
    id: sanitizeText(feedback.id, FIELD_LIMITS.id),
    type: sanitizeFeedbackType(feedback.type),
    message: sanitizeFeedbackMessage(feedback.message, FIELD_LIMITS.feedback),
    userId: sanitizeText(feedback.userId, FIELD_LIMITS.id),
    appVersion: sanitizeText(feedback.appVersion, 30),
    platform: sanitizeFeedbackMessage(feedback.platform, 120),
    feedbackDate,
    createdAt: sanitizeText(feedback.createdAt || new Date().toISOString(), 40),
    createdAtMs: sanitizeAmount(feedback.createdAtMs || Date.now(), 9999999999999, 0),
    slot: sanitizeInteger(feedback.slot || 0, 3)
  };
  if (!safe.id) safe.id = `${feedbackDate}_${safe.createdAtMs}`.slice(0, FIELD_LIMITS.id);
  return safe;
}

export function sanitizeEmail(value, maxLength = 254) {
  return String(value || "")
    .replace(CONTROL_CHARS, "")
    .replace(ZERO_WIDTH, "")
    .trim()
    .toLowerCase()
    .slice(0, maxLength);
}

export function sanitizePhone(value) {
  // WhatsApp phone number: digits with one optional leading + only.
  const raw = String(value || "").trim();
  const hasLeadingPlus = raw.startsWith("+");
  const digits = raw.replace(/\D/g, "").slice(0, 15);
  return `${hasLeadingPlus ? "+" : ""}${digits}`.slice(0, FIELD_LIMITS.phone);
}

export function sanitizeDate(value) {
  const text = String(value || "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
}

export function sanitizeChoice(value, allowed = [], fallback = "") {
  return allowed.includes(value) ? value : fallback;
}

export function sanitizeAmount(value, max = 999999999999, decimals = 2) {
  if (value === null || value === undefined || value === "") return 0;
  const raw = String(value).trim();
  // Do not turn malicious/invalid numeric text like -100, +100, or 1e9 into a positive amount.
  if (/[-−+]/.test(raw) || /[a-z]/i.test(raw)) return 0;
  const normalizedSeparators = raw.replace(/,/g, "");
  const cleaned = normalizedSeparators.replace(/[^0-9.]/g, "");
  const [first, ...rest] = cleaned.split(".");
  const normalized = rest.length ? `${first}.${rest.join("")}` : first;
  const num = Number(normalized);
  if (!Number.isFinite(num) || num < 0) return 0;
  const capped = Math.min(num, max);
  const factor = 10 ** decimals;
  return Math.round(capped * factor) / factor;
}

export function sanitizeInteger(value, max = 999999) {
  return Math.round(sanitizeAmount(value, max, 0));
}

export function sanitizePercentage(value) {
  return sanitizeAmount(value, 100, 2);
}

export function sanitizeGram(value) {
  if (value === null || value === undefined || value === "") return 0;

  const raw = String(value).trim();
  // Match amount-field safety: reject negative, signed, scientific-notation,
  // formula-like, or alphabetic payloads instead of silently converting them.
  if (/[-−+]/.test(raw) || /[a-z]/i.test(raw) || /^[=@]/.test(raw)) return 0;

  // Preserve the exact 4-decimal gram value typed by the user.
  // Avoid Math.trunc(num * 10000) because floating point values like 39.1339
  // can become 391338.999999 and incorrectly save/display as 39.1338.
  const cleaned = raw.replace(/[^0-9.]/g, "");
  const [firstRaw = "", ...rest] = cleaned.split(".");
  const whole = firstRaw.replace(/^0+(?=\d)/, "").slice(0, 9) || "0";
  const fraction = rest.join("").slice(0, 4);
  const normalized = fraction ? `${whole}.${fraction}` : whole;
  const num = Number(normalized);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.min(num, 999999.9999);
}

export function hasSpreadsheetFormulaPrefix(value) {
  if (value === null || value === undefined) return false;
  const text = String(value).replace(CONTROL_CHARS, " ").replace(ZERO_WIDTH, "");
  return /^[=+\-@]/.test(text.trimStart());
}

export function safeForExport(value) {
  if (value === null || value === undefined) return "";
  const text = String(value).replace(CONTROL_CHARS, " ").replace(ZERO_WIDTH, "");
  return hasSpreadsheetFormulaPrefix(text) ? `'${text}` : text;
}

export function safeSheetName(value, fallback = "Sheet") {
  const name = safeForExport(sanitizeText(value, 31)).replace(/[:\\/?*[\]]/g, "").trim();
  return (name || fallback).slice(0, 31);
}

export function sanitizeRecordForExport(record = {}) {
  const safeRecord = {};
  Object.entries(record || {}).forEach(([key, value]) => {
    if (typeof value === "string") safeRecord[key] = safeForExport(sanitizeText(value, 1000));
    else safeRecord[key] = value;
  });
  return safeRecord;
}

function sanitizeHistoryItem(item = {}) {
  return {
    id: sanitizeText(item.id, FIELD_LIMITS.id) || sanitizeText(item.date, 40),
    date: sanitizeDate(item.date) || sanitizeText(item.date, 40),
    value: sanitizeAmount(item.value)
  };
}

function sanitizeBudgets(budgets = {}) {
  if (!budgets || typeof budgets !== "object" || Array.isArray(budgets)) return {};
  const safe = {};
  Object.entries(budgets).slice(-36).forEach(([month, data]) => {
    if (!SAFE_MONTH_KEY.test(month) || !data || typeof data !== "object" || Array.isArray(data)) return;
    const categories = {};
    Object.entries(data.categories || {}).slice(0, 40).forEach(([name, value]) => {
      const cleanName = sanitizeText(name, FIELD_LIMITS.category);
      const amount = sanitizeAmount(value);
      if (cleanName && amount > 0) categories[cleanName] = amount;
    });
    safe[month] = { monthly: sanitizeAmount(data.monthly), categories };
  });
  return safe;
}

export function sanitizeAccountForStorage(account = {}) {
  const safe = {
    name: sanitizeProfileName(account.name, FIELD_LIMITS.profileName),
    icon: sanitizeText(account.icon || "👤", 4) || "👤",
    color: SAFE_HEX_COLOR.test(String(account.color || "")) ? account.color : "#4F46E5",
    profileType: sanitizeChoice(account.profileType || account.type || "standard", ALLOWED_PROFILE_TYPES, "standard"),
    phone: sanitizePhone(account.phone),
    isPrimary: account.isPrimary === true,
    currentValue: sanitizeAmount(account.currentValue),
    cvHistory: Array.isArray(account.cvHistory) ? account.cvHistory.slice(0, 90).map(sanitizeHistoryItem).filter(h => h.date || h.value > 0) : [],
    budgets: sanitizeBudgets(account.budgets),
    // Image uploads are disabled for now. Do not persist new profile photos.
    photo: null
  };

  const id = sanitizeText(account.id, FIELD_LIMITS.id);
  if (id) safe.id = id;

  if (Array.isArray(account.recurringRules)) {
    safe.recurringRules = account.recurringRules.slice(0, 60).map(sanitizeRecurringRuleForStorage).filter(Boolean);
  }

  return safe;
}

export function sanitizeRecurringRuleForStorage(rule = {}) {
  if (!rule || typeof rule !== "object") return null;
  const id = sanitizeText(rule.id, FIELD_LIMITS.id);
  const title = sanitizeText(rule.title, FIELD_LIMITS.title);
  const amount = sanitizeAmount(rule.amount);
  if (!title || amount <= 0) return null;
  return {
    id,
    title,
    type: sanitizeChoice(rule.type, ALLOWED_STANDARD_TYPES, "expense"),
    category: sanitizeText(rule.category, FIELD_LIMITS.category),
    amount,
    frequency: sanitizeChoice(rule.frequency, ["weekly", "monthly", "yearly"], "monthly"),
    nextDate: sanitizeDate(rule.nextDate),
    paymentMethod: sanitizeText(rule.paymentMethod, 40),
    note: sanitizeNote(rule.note, FIELD_LIMITS.note),
    active: rule.active !== false,
    createdAt: sanitizeText(rule.createdAt, 40),
    lastPostedAt: sanitizeText(rule.lastPostedAt, 40)
  };
}

function withMigrationMetadata(safe, tx = {}) {
  const migratedFromProfileId = sanitizeText(tx.migratedFromProfileId, FIELD_LIMITS.id);
  const migratedFromProfileName = sanitizeText(tx.migratedFromProfileName, FIELD_LIMITS.profileName);
  const migratedAt = sanitizeText(tx.migratedAt, 40);
  if (migratedFromProfileId) safe.migratedFromProfileId = migratedFromProfileId;
  if (migratedFromProfileName) safe.migratedFromProfileName = migratedFromProfileName;
  if (migratedAt) safe.migratedAt = migratedAt;
  return safe;
}

export function sanitizeTransactionForStorage(tx = {}, profileType = "standard") {
  const effectiveProfileType = sanitizeChoice(profileType, ALLOWED_PROFILE_TYPES, "standard");
  const safe = {
    note: sanitizeNote(tx.note, FIELD_LIMITS.note),
    amount: sanitizeAmount(tx.amount),
    date: sanitizeDate(tx.date) || sanitizeDate(new Date().toISOString().split("T")[0]),
    // Receipt/image uploads are disabled for now. Do not persist new receipt images.
    receipt: null
  };

  const id = sanitizeText(tx.id, FIELD_LIMITS.id);
  if (id) safe.id = id;

  if (effectiveProfileType === "standard") {
    safe.type = sanitizeChoice(tx.type, ALLOWED_STANDARD_TYPES, "expense");
    safe.category = sanitizeText(tx.category, FIELD_LIMITS.category);
    safe.paymentMethod = sanitizeText(tx.paymentMethod, 40);
  } else if (effectiveProfileType === "lending") {
    safe.txType = sanitizeChoice(tx.txType, ALLOWED_LENDING_TYPES, "lent");
    safe.person = sanitizeText(tx.person, FIELD_LIMITS.person);
    safe.phone = sanitizePhone(tx.phone);
    safe.dueDate = sanitizeDate(tx.dueDate);
    safe.status = sanitizeText(tx.status, 30);
    safe.cashback = sanitizeAmount(tx.cashback);
  } else if (effectiveProfileType === "trading") {
    safe.txType = sanitizeChoice(tx.txType, ALLOWED_TRADE_TYPES, "deposit");
    safe.symbol = sanitizeText(tx.symbol, FIELD_LIMITS.symbol).toUpperCase();
    safe.assetType = sanitizeText(tx.assetType, 40);
    safe.exchange = sanitizeText(tx.exchange, 20).toUpperCase();
    safe.qty = sanitizeAmount(tx.qty, 999999999, 4);
    safe.buyPrice = sanitizeAmount(tx.buyPrice);
    safe.stopLoss = sanitizeAmount(tx.stopLoss);
    safe.target = sanitizeAmount(tx.target);
  } else if (["goldsilver", "gold", "silver"].includes(effectiveProfileType)) {
    safe.txType = sanitizeChoice(tx.txType, ALLOWED_METAL_TX_TYPES, "buy");
    safe.metalType = effectiveProfileType === "gold" ? "Gold" : effectiveProfileType === "silver" ? "Silver" : sanitizeChoice(tx.metalType || "Gold", ["Gold", "Silver"], "Gold");
    safe.grams = sanitizeGram(tx.grams);
    safe.pricePerGram = sanitizeAmount(tx.pricePerGram);
    safe.cashback = safe.txType === "buy" ? sanitizeAmount(tx.cashback) : 0;
    safe.platform = sanitizeText(tx.platform, FIELD_LIMITS.platform);
  } else if (effectiveProfileType === "fdrd") {
    safe.txType = sanitizeChoice(tx.txType, ALLOWED_FDRD_TYPES, "active");
    safe.depositType = sanitizeChoice(tx.depositType, ALLOWED_DEPOSIT_TYPES, "FD");
    safe.bankName = sanitizeText(tx.bankName, FIELD_LIMITS.bankName);
    safe.depositName = sanitizeText(tx.depositName, FIELD_LIMITS.depositName);
    safe.interestRate = sanitizePercentage(tx.interestRate);
    safe.maturityDate = sanitizeDate(tx.maturityDate);
    safe.expectedMaturityAmount = sanitizeAmount(tx.expectedMaturityAmount);
    safe.interestPayout = sanitizeText(tx.interestPayout, 40);
    safe.monthlyDeposit = sanitizeAmount(tx.monthlyDeposit);
    safe.totalMonths = sanitizeInteger(tx.totalMonths, 1200);
    safe.installmentDay = sanitizeInteger(tx.installmentDay, 31);
    safe.actualReturnedAmount = sanitizeAmount(tx.actualReturnedAmount);
    safe.closureDate = sanitizeDate(tx.closureDate);
    safe.penalty = sanitizeAmount(tx.penalty);
    safe.renewedAmount = sanitizeAmount(tx.renewedAmount);
    safe.expectedReturnPct = sanitizePercentage(tx.expectedReturnPct);
    safe.actualReturnPct = sanitizePercentage(tx.actualReturnPct);
  }

  return withMigrationMetadata(safe, tx);
}

export function sanitizeBackupForExport(accounts = [], txnMap = {}) {
  const safeAccounts = (accounts || []).map(sanitizeAccountForStorage);
  const safeTxnMap = {};
  safeAccounts.forEach((account) => {
    safeTxnMap[account.id] = (txnMap[account.id] || []).map((tx) => sanitizeTransactionForStorage(tx, account.profileType));
  });
  return { accounts: safeAccounts, transactions: safeTxnMap };
}
